import type { CabRepeatValue } from '../types/cabSchedule';

export interface MeetingOccurrence {
  start: Date;
  end: Date;
}

export interface SpecificRepeatOptions {
  interval: number;
  weekdays: number[];
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

/** Add months while clamping to month-end when the source day does not exist (e.g. Jan 31 → Feb 28/29). */
export function addMonthsPreserveDay(d: Date, months: number): Date {
  const next = new Date(d);
  const day = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() !== day) {
    next.setDate(0);
  }
  return next;
}

/** Add years while clamping Feb 29 → Feb 28 in non-leap years. */
export function addYearsPreserveDay(d: Date, years: number): Date {
  const next = new Date(d);
  const day = next.getDate();
  next.setFullYear(next.getFullYear() + years);
  if (next.getDate() !== day) {
    next.setDate(0);
  }
  return next;
}

function isOnOrBeforeEndDate(cursor: Date, repeatsUntil: Date): boolean {
  return startOfDay(cursor).getTime() <= startOfDay(repeatsUntil).getTime();
}

function startOfWeekMonday(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return startOfDay(addDays(d, diff));
}

function weekIndexFromSeriesStart(date: Date, seriesStart: Date): number {
  const a = startOfWeekMonday(seriesStart).getTime();
  const b = startOfWeekMonday(date).getTime();
  return Math.round((b - a) / (7 * 24 * 60 * 60 * 1000));
}

function matchesRepeatDay(repeat: CabRepeatValue, date: Date): boolean {
  const day = date.getDay();
  switch (repeat) {
    case 'weekdays':
      return day >= 1 && day <= 5;
    case 'weekends':
      return day === 0 || day === 6;
    case 'weekMWF':
      return day === 1 || day === 3 || day === 5;
    case 'weekTT':
      return day === 2 || day === 4;
    default:
      return true;
  }
}

function occurrenceEnd(start: Date, templateStart: Date, templateEnd: Date, allDay: boolean): Date {
  if (allDay) {
    return endOfDay(start);
  }
  const durationMs = templateEnd.getTime() - templateStart.getTime();
  return new Date(start.getTime() + durationMs);
}

function pushOccurrence(
  occurrences: MeetingOccurrence[],
  start: Date,
  templateStart: Date,
  templateEnd: Date,
  allDay: boolean
): void {
  const end = occurrenceEnd(start, templateStart, templateEnd, allDay);
  if (end.getTime() > start.getTime()) {
    occurrences.push({ start: new Date(start), end });
  }
}

function slotStartForDay(cursor: Date, effectiveStart: Date, allDay: boolean): Date {
  if (allDay) {
    return startOfDay(cursor);
  }
  return new Date(
    cursor.getFullYear(),
    cursor.getMonth(),
    cursor.getDate(),
    effectiveStart.getHours(),
    effectiveStart.getMinutes(),
    effectiveStart.getSeconds(),
    0
  );
}

function generateSpecificOccurrences(
  effectiveStart: Date,
  effectiveEnd: Date,
  repeatsUntil: Date,
  allDay: boolean,
  specific: SpecificRepeatOptions
): MeetingOccurrence[] {
  const occurrences: MeetingOccurrence[] = [];
  const weekdaySet = new Set(specific.weekdays.filter((d) => d >= 0 && d <= 6));
  const interval = Math.max(1, Math.floor(specific.interval) || 1);
  if (weekdaySet.size === 0) return occurrences;

  const horizonEnd = endOfDay(repeatsUntil);
  let cursor = startOfDay(effectiveStart);

  while (isOnOrBeforeEndDate(cursor, horizonEnd)) {
    if (weekdaySet.has(cursor.getDay())) {
      const weekIdx = weekIndexFromSeriesStart(cursor, effectiveStart);
      if (weekIdx >= 0 && weekIdx % interval === 0) {
        const slotStart = slotStartForDay(cursor, effectiveStart, allDay);
        pushOccurrence(occurrences, slotStart, effectiveStart, effectiveEnd, allDay);
      }
    }
    cursor = addDays(cursor, 1);
  }

  return occurrences;
}

export function generateMeetingOccurrences(
  templateStart: Date,
  templateEnd: Date,
  repeat: CabRepeatValue,
  repeatsUntil: Date | null,
  allDay: boolean,
  specific?: SpecificRepeatOptions | null
): MeetingOccurrence[] {
  const occurrences: MeetingOccurrence[] = [];
  const effectiveStart = allDay ? startOfDay(templateStart) : new Date(templateStart);
  const effectiveEnd = allDay ? endOfDay(templateEnd) : new Date(templateEnd);

  if (repeat === '') {
    pushOccurrence(occurrences, effectiveStart, effectiveStart, effectiveEnd, allDay);
    return occurrences;
  }

  if (!repeatsUntil) {
    return occurrences;
  }

  if (repeat === 'specific') {
    return generateSpecificOccurrences(effectiveStart, effectiveEnd, repeatsUntil, allDay, {
      interval: specific?.interval ?? 1,
      weekdays: specific?.weekdays ?? [],
    });
  }

  const horizonEnd = endOfDay(repeatsUntil);

  if (repeat === 'weekly') {
    let cursor = new Date(effectiveStart);
    while (isOnOrBeforeEndDate(cursor, horizonEnd)) {
      pushOccurrence(occurrences, cursor, effectiveStart, effectiveEnd, allDay);
      cursor = addDays(cursor, 7);
    }
    return occurrences;
  }

  if (repeat === 'monthly') {
    let cursor = new Date(effectiveStart);
    while (isOnOrBeforeEndDate(cursor, horizonEnd)) {
      pushOccurrence(occurrences, cursor, effectiveStart, effectiveEnd, allDay);
      cursor = addMonthsPreserveDay(cursor, 1);
    }
    return occurrences;
  }

  if (repeat === 'yearly') {
    let cursor = new Date(effectiveStart);
    while (isOnOrBeforeEndDate(cursor, horizonEnd)) {
      pushOccurrence(occurrences, cursor, effectiveStart, effectiveEnd, allDay);
      cursor = addYearsPreserveDay(cursor, 1);
    }
    return occurrences;
  }

  let cursor = startOfDay(effectiveStart);
  while (isOnOrBeforeEndDate(cursor, horizonEnd)) {
    if (repeat === 'daily' || matchesRepeatDay(repeat, cursor)) {
      const slotStart = slotStartForDay(cursor, effectiveStart, allDay);
      pushOccurrence(occurrences, slotStart, effectiveStart, effectiveEnd, allDay);
    }
    cursor = addDays(cursor, 1);
  }

  return occurrences;
}
